// src/_components/main/MyTripComponent.js
import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Dropdown,
  Alert,
  Form,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import "../../styles/MyTripComponent.css";
import SavedComponent from "./SavedComponent";

const MyTripComponent = ({
  myTripItems,
  onRemoveFromTrip,
  onUpdateTripItem,
  onLocationSelect,
  savedItems = [], // 新增：从收藏页面传入的保存项目
  onAddToTrip, // 新增：从收藏添加到行程的回调
}) => {
  const navigate = useNavigate();
  const getImageUrl = (item) => {
    return (
      item.image ||
      item.image_url ||
      item.fullData?.image_url ||
      item.fullData?.image ||
      "https://static.seeker.io/media/img/1/a03612f1-73a9-4f3a-b7fa-53b475c137a4-1400.jpg"
    );
  };

  const [selectedItems, setSelectedItems] = useState([]);
  const [showBudgetPreview, setShowBudgetPreview] = useState(false);
  const [selectedTier, setSelectedTier] = useState("Basic");
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [selectedDayForAdd, setSelectedDayForAdd] = useState(null);

  // 计算预算统计
  const budgetStats = useMemo(() => {
    const selectedTrip = myTripItems.filter((item) =>
      selectedItems.includes(item.id)
    );

    const totalCost = selectedTrip.reduce((sum, item) => {
      const cost = parseFloat(item.cost || item.avg_cost_per_person || 0);
      return sum + cost;
    }, 0);

    const categories = selectedTrip.reduce((acc, item) => {
      const category = item.category || item.type || "Other";
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return {
      totalItems: selectedTrip.length,
      totalCost,
      categories,
      selectedItems: selectedTrip,
    };
  }, [myTripItems, selectedItems]);

  // 处理项目选择
  const handleItemSelect = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedItems.length === myTripItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(myTripItems.map((item) => item.id));
    }
  };

  // 跳转到预算页面
  const handleGoToBudget = () => {
    const selectedTrip = myTripItems.filter((item) =>
      selectedItems.includes(item.id)
    );

    console.log(
      "🔍 Selected trip items for budget:",
      selectedTrip.map((item) => ({
        name: item.name,
        dayNumber: item.dayNumber,
        fromItinerary: item.fromItinerary,
      }))
    );

    const budgetData = {
      selectedTier,
      customActivities: selectedTrip.map((item) => ({
        place_id: item.id,
        name: item.name,
        location: item.location,
        activity: item.category || item.type || "Activity",
        avg_cost_per_person: parseFloat(
          item.cost || item.avg_cost_per_person || 0
        ),
        image_url: item.image || item.image_url,
        activity_desc: item.description,
        url: item.website || item.url,
        start_time: item.plannedTime || "09:00 AM",
        end_time: item.plannedEndTime || "10:00 AM",
        planned_date: item.plannedDate,
        day_number: item.dayNumber || 1, // 🔑 检查这个字段
        original_day: item.dayNumber, // 🔑 备用字段
      })),
      totalEstimatedCost: budgetStats.totalCost,
      fromMyTrip: true,
    };

    console.log(
      "🔍 Budget data with day_number:",
      budgetData.customActivities.map((item) => ({
        name: item.name,
        day_number: item.day_number,
        dayNumber: item.dayNumber,
      }))
    );

    console.log("🔍 Budget data being passed:", budgetData);

    navigate("/budget-tiers", { state: budgetData });
  };

  // 从收藏添加到我的行程
  const handleAddFromSaved = (savedItem) => {
    const tripItem = {
      ...savedItem,
      addedAt: new Date().toISOString(),
      plannedDate: null,
      plannedTime: savedItem.start_time || null,
      plannedEndTime: savedItem.end_time || null,
      duration:
        savedItem.start_time && savedItem.end_time
          ? `${savedItem.start_time} - ${savedItem.end_time}`
          : null,
      notes: "",
    };
    onAddToTrip?.(tripItem);
  };

  const groupItemsByDate = (items) => {
    // 先按是否来自行程分组
    const fromItinerary = items.filter((item) => item.fromItinerary);
    const userAdded = items.filter((item) => !item.fromItinerary);

    const groups = {};

    // 处理来自行程的项目 - 按天数分组
    fromItinerary.forEach((item) => {
      const dayKey = `day-${item.dayNumber}`;
      if (!groups[dayKey]) {
        groups[dayKey] = {
          type: "itinerary-day",
          dayNumber: item.dayNumber,
          title: `Day ${item.dayNumber}`,
          items: [],
        };
      }
      groups[dayKey].items.push(item);
    });

    // 处理用户添加的项目 - 按日期分组或放入未安排
    userAdded.forEach((item) => {
      const dateKey = item.plannedDate || "unscheduled";
      if (!groups[dateKey]) {
        groups[dateKey] = {
          type: "date-group",
          date: item.plannedDate,
          title: item.plannedDate
            ? new Date(item.plannedDate).toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })
            : "Unscheduled",
          items: [],
        };
      }
      groups[dateKey].items.push(item);
    });

    return groups;
  };

  return (
    <Container fluid className="my-trip-container h-100 p-0">
      {/* 页面头部 */}
      <div className="my-trip-header d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <div>
          <h4 className="mb-0">
            <i className="bi bi-suitcase-lg me-2"></i>
            My Trip Planning
          </h4>
          <small className="text-muted">
            {myTripItems.length} items • {selectedItems.length} selected
          </small>
        </div>

        <div className="d-flex gap-2">
          {myTripItems.length > 0 && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedItems.length === myTripItems.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={() => setShowBudgetPreview(!showBudgetPreview)}
                disabled={selectedItems.length === 0}
              >
                <i className="bi bi-calculator me-1"></i>
                Budget Preview
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 预算预览面板 */}
      {showBudgetPreview && selectedItems.length > 0 && (
        <div className="budget-preview-panel p-3 bg-light border-bottom">
          <Row>
            <Col md={8}>
              <h6>
                <i className="bi bi-graph-up me-2"></i>Budget Preview
              </h6>
              <div className="d-flex gap-4 mb-2">
                <div>
                  <strong>{budgetStats.totalItems}</strong> activities selected
                </div>
                <div>
                  Estimated total:{" "}
                  <strong>${budgetStats.totalCost.toFixed(2)}</strong>
                </div>
              </div>

              <div className="category-breakdown d-flex gap-2 flex-wrap">
                {Object.entries(budgetStats.categories).map(
                  ([category, count]) => (
                    <Badge key={category} bg="secondary" className="small">
                      {category}: {count}
                    </Badge>
                  )
                )}
              </div>
            </Col>

            <Col md={4} className="text-end">
              <Form.Select
                size="sm"
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="mb-2"
                style={{ width: "auto", display: "inline-block" }}
              >
                <option value="Basic">Basic Tier</option>
                <option value="Signature">Signature Tier</option>
                <option value="Luxury">Luxury Tier</option>
              </Form.Select>

              <Button
                variant="primary"
                onClick={handleGoToBudget}
                className="d-block w-100"
              >
                <i className="bi bi-arrow-right me-1"></i>
                Create Budget Plan
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <div className="my-trip-content flex-grow-1 overflow-auto p-3">
        {/* 如果有保存的项目但没有行程项目，显示添加建议 */}
        {myTripItems.length === 0 && savedItems.length > 0 && (
          <Alert variant="info" className="mb-4">
            <h6>
              <i className="bi bi-lightbulb me-2"></i>Start Planning Your Trip
            </h6>
            <p className="mb-2">
              You have {savedItems.length} saved items. Add them to your trip to
              start planning!
            </p>
            <div className="d-flex gap-2 flex-wrap">
              {savedItems.slice(0, 3).map((item) => (
                <Button
                  key={item.id}
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleAddFromSaved(item)}
                >
                  + Add {item.name}
                </Button>
              ))}
              {savedItems.length > 3 && (
                <small className="text-muted align-self-center">
                  and {savedItems.length - 3} more...
                </small>
              )}
            </div>
          </Alert>
        )}

        {/* 行程项目列表 */}
        {myTripItems.length === 0 ? (
          <div className="empty-state text-center py-5">
            <div className="empty-state-icon mb-3">
              <i
                className="bi bi-suitcase-lg"
                style={{ fontSize: "3rem", color: "#ccc" }}
              ></i>
            </div>
            <h5 className="text-muted mb-2">No Trip Items Yet</h5>
            <p className="text-muted mb-3">
              Start adding locations from the Explore page or your Saved items
              to create your personalized trip.
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <Button
                variant="outline-primary"
                onClick={() => navigate("/chat")} // 假设这会切换到explore视图
              >
                <i className="bi bi-compass me-1"></i>
                Explore Locations
              </Button>
              {savedItems.length > 0 && (
                <Button
                  variant="primary"
                  onClick={() => navigate("/chat")} // 假设这会切换到saved视图
                >
                  <i className="bi bi-heart me-1"></i>
                  View Saved ({savedItems.length})
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="itinerary-container">
            {Object.entries(groupItemsByDate(myTripItems))
              .sort(([a], [b]) => {
                // 先显示Day 1, Day 2... 然后是日期，最后是unscheduled
                if (a.startsWith("day-") && !b.startsWith("day-")) return -1;
                if (!a.startsWith("day-") && b.startsWith("day-")) return 1;
                if (a.startsWith("day-") && b.startsWith("day-")) {
                  return parseInt(a.split("-")[1]) - parseInt(b.split("-")[1]);
                }
                if (a === "unscheduled") return 1;
                if (b === "unscheduled") return -1;
                return a.localeCompare(b);
              })
              .map(([groupKey, group]) => (
                <div key={groupKey} className="day-section mb-3">
                  {/* 日期头部 */}
                  <div
                    className="day-header d-flex align-items-center justify-content-between p-3 bg-light rounded-top border"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setCollapsedGroups((prev) => {
                        const newSet = new Set(prev);
                        if (newSet.has(groupKey)) {
                          newSet.delete(groupKey);
                        } else {
                          newSet.add(groupKey);
                        }
                        return newSet;
                      });
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <i
                        className={`bi ${
                          collapsedGroups.has(groupKey)
                            ? "bi-chevron-right"
                            : "bi-chevron-down"
                        } me-2`}
                      ></i>
                      <strong>{group.title}</strong>

                      {group.type === "itinerary-day" && (
                        <span className="ms-2 badge bg-primary">
                          From Itinerary
                        </span>
                      )}
                    </div>
                    <small className="text-muted">
                      {group.items.length} activities
                    </small>
                  </div>

                  {/* 当天活动列表 */}
                  {!collapsedGroups.has(groupKey) && (
                    <div className="day-activities border border-top-0 rounded-bottom">
                      {group.items.map((item, index) => (
                        <div
                          key={item.id}
                          className="activity-item d-flex align-items-center p-3 border-bottom"
                        >
                          {/* 活动图片 */}
                          <img
                            src={getImageUrl(item)}
                            className="activity-image rounded me-3"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src =
                                "https://static.seeker.io/media/img/1/a03612f1-73a9-4f3a-b7fa-53b475c137a4-1400.jpg";
                            }}
                          />

                          {/* 活动信息 */}
                          <div className="activity-info flex-grow-1">
                            <h6 className="mb-1" style={{ fontSize: "0.9rem" }}>
                              {item.name}
                            </h6>
                            <small className="text-muted d-block">
                              {item.location}
                            </small>
                            <small className="text-muted">
                              {item.category}
                            </small>
                          </div>

                          {/* 操作按钮 */}
                          <div className="activity-actions d-flex align-items-center gap-2">
                            <Form.Check
                              type="checkbox"
                              checked={selectedItems.includes(item.id)}
                              onChange={() => handleItemSelect(item.id)}
                            />
                            {item.cost && (
                              <small className="text-success fw-bold">
                                ${item.cost}
                              </small>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => onRemoveFromTrip(item.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </div>
                      ))}
                      {/* Add 按钮 */}
                      <div className="add-activity-section p-3 border-top bg-light">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="d-flex align-items-center"
                          onClick={() => {
                            setSelectedDayForAdd(groupKey); // 保存当前天的标识
                            setShowSavedModal(true);
                          }}
                        >
                          <i className="bi bi-plus me-1"></i>
                          Add
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}

        {/* 底部行动建议 */}
        {myTripItems.length > 0 && selectedItems.length === 0 && (
          <Alert variant="light" className="mt-4 text-center">
            <p className="mb-2">
              <i className="bi bi-info-circle me-2"></i>
              Select items above to create a budget plan and proceed to checkout
            </p>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleSelectAll}
            >
              Select All Items
            </Button>
          </Alert>
        )}
      </div>

      {/* 从Saved添加活动的模态框 */}
      <Modal
        show={showSavedModal}
        onHide={() => setShowSavedModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add from Saved Places</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SavedComponent
            favorites={savedItems?.map((item) => item.id) || []}
            allRecommendations={savedItems || []}
            onAddToMyTrip={(item) => {
              const tripItem = {
                ...item,
                id: `saved-${Date.now()}`,
                addedAt: new Date().toISOString(),
                dayNumber: selectedDayForAdd.includes("day-")
                  ? parseInt(selectedDayForAdd.split("-")[1])
                  : null,
                fromItinerary: selectedDayForAdd.includes("day-"),
                plannedDate: null,
                plannedTime: null,
              };
              onAddToTrip(tripItem);
              setShowSavedModal(false);
            }}
            compact={true}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default MyTripComponent;
